
import { useNovel } from '@/context/novel-context';
import { sortEntitiesByUpdatedAt } from '@/lib/date-utils';
import type { EntitySummary, EventSummary } from '@common/schemas/entities/summary.schema';
import { Close, Search } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { MapEntityRef } from '../canvas/types';

interface WorldEntityPickerProps {
    open: boolean;
    onClose: () => void;
    onSelect: (ref: MapEntityRef) => void;
    /** Restrict to only 'locations' (for area linking) */
    locationsOnly?: boolean;
    /** The ID of the Location whose map is being edited (for filtering) */
    currentLocationId?: string;
}

type TabId = 'locations' | 'events';

export function WorldEntityPicker({
    open, onClose, onSelect, locationsOnly = false, currentLocationId,
}: WorldEntityPickerProps) {
    const { t } = useTranslation();
    const { getSummaries } = useNovelRepository();
    const [tab, setTab] = useState<TabId>('locations');
    const [showAll, setShowAll] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<MapEntityRef | null>(null);

    const locationSummaries = getSummaries<EntitySummary>('locations');
    const eventSummaries = getSummaries<EventSummary>('events');

    // Collect ancestor/descendant location IDs for the current location
    const relatedLocationIds = useMemo(() => {
        if (!currentLocationId || showAll) return null;
        const ids = new Set<string>();
        // Find all locations whose parentId chain includes currentLocationId
        const addDescendants = (parentId: string) => {
            locationSummaries.forEach(loc => {
                if (loc.parentId === parentId && !ids.has(loc.id)) {
                    ids.add(loc.id);
                    addDescendants(loc.id);
                }
            });
        };
        addDescendants(currentLocationId);
        ids.add(currentLocationId);
        return ids;
    }, [currentLocationId, locationSummaries, showAll]);

    const filteredLocations = useMemo(() => {
        let list = locationSummaries.filter(l => l.id !== currentLocationId || showAll);
        if (relatedLocationIds && !showAll) {
            list = list.filter(l => relatedLocationIds.has(l.id));
        }
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(l => l.name.toLowerCase().includes(q));
        }
        return list;
    }, [locationSummaries, relatedLocationIds, showAll, search, currentLocationId]);

    const filteredEvents = useMemo(() => {
        let list = eventSummaries;
        if (!showAll && currentLocationId) {
            list = list.filter(e => {
                return e.locationIds?.includes(currentLocationId) ||
                    (relatedLocationIds ? [...(e.locationIds ?? [])].some(id => relatedLocationIds.has(id)) : false);
            });
        }
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(e => e.name.toLowerCase().includes(q));
        }
        return list;
    }, [eventSummaries, showAll, currentLocationId, relatedLocationIds, search]);

    const handleConfirm = () => {
        if (!selected) return;
        onSelect(selected);
        setSelected(null);
        setSearch('');
        onClose();
    };

    const handleClose = () => {
        setSelected(null);
        setSearch('');
        onClose();
    };

    const activeList = tab === 'locations' ? filteredLocations : filteredEvents;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Typography fontWeight="bold">{t('map_creator.world_entity_picker.title')}</Typography>
                <IconButton size="small" onClick={handleClose}><Close fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 360 }}>
                {!locationsOnly && (
                    <Tabs value={tab} onChange={(_, v: TabId) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                        <Tab label={t('map_creator.world_entity_picker.tabs.locations')} value="locations" />
                        <Tab label={t('map_creator.world_entity_picker.tabs.events')} value="events" />
                    </Tabs>
                )}

                <Box px={2} pt={1.5} pb={0.5} display="flex" alignItems="center" gap={1}>
                    <TextField
                        size="small"
                        placeholder={t('map_creator.world_entity_picker.search_placeholder')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        fullWidth
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                        }}
                    />
                </Box>

                <Box px={2} pb={0.5}>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={showAll} onChange={e => setShowAll(e.target.checked)} />}
                        label={<Typography variant="caption">{t('map_creator.world_entity_picker.show_all')}</Typography>}
                    />
                </Box>

                <List dense sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
                    {activeList.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
                            {t('map_creator.world_entity_picker.empty')}
                        </Typography>
                    )}
                    {activeList.map(item => {
                        const isSelected = selected?.id === item.id;
                        return (
                            <ListItemButton
                                key={item.id}
                                selected={isSelected}
                                onClick={() => setSelected({ type: tab, id: item.id, name: item.name })}
                                sx={{ borderRadius: 1, mb: 0.5 }}
                            >
                                <ListItemText
                                    primary={item.name}
                                    secondary={item.shortDescription?.slice(0, 60) || item.type}
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 'bold' : 'normal' }}
                                    secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.65rem' } }}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </DialogContent>

            <DialogActions sx={{ px: 2, pb: 2 }}>
                <Button onClick={handleClose} color="inherit" size="small">{t('entity_form.common.cancel')}</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    size="small"
                    disabled={!selected}
                >
                    {t('map_creator.world_entity_picker.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
